import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Grid,
  Button,
  FormControl,
  Typography,
  TextField,
} from "@mui/material";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { PictureAsPdf as PictureAsPdfIcon } from "@mui/icons-material";
import SaveIcon from '@mui/icons-material/Save';
import CompleteIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from '@mui/icons-material/Delete';
import { useParams, useNavigate } from "react-router-dom";
import { usePrompt } from "../../../../../../common/usePrompt"
import Confirmation from "../../../../../../common/Comfirmation";
import { GetStudy, UpdateAddendum, CompleteStudy } from "../../../../../../api";
import RichText from "../../../../../../common/EditorMCE";
import ReportingData from "../Respond/ReportingData";
import PdfPreview from "../../../shared/PdfPreview"
import TextFieldsIcon from '@mui/icons-material/TextFields';

let changeTimeout
const defaultFields = [
  { label: "Addendum" },
]

const isEditorAlive = editor => editor && !editor.destroyed && editor.getBody()
const isEditorEmpty = editor => {
  if (!isEditorAlive(editor)) return true
  return editor.getContent({ format: 'text' }).trim() === '' && !editor.getBody().querySelector('img');
}

const StudyAddendum = () => {
  const params = useParams();
  const [content, setContent] = useState([]);
  const [study, setStudy] = useState(false);
  const [open, setOpen] = useState(false);
  const [newField, setNewField] = useState("");
  const navigate = useNavigate();
  const [pdfPreviewDialogState, setPdfPreviewDialogState] = useState({
    open: false,
    studyId: null,
  });
  const [changes, setChanges] = useState(true)
  const [autoSave, setAutoSave] = useState()
  const autoSaveRef = React.useRef(false)
  const [confirm, setConfirm] = useState({
    open: false,
    text: "",
    callback: undefined
  })

  const didRun = React.useRef(false); // Prevent double render in dev mode

  const confirmOk = () => {
    confirm.callback && confirm.callback()
    confirmCancel()
  }
  const confirmCancel = () => {
    setConfirm(
      (i) => (i = { open: false, text: "", callback: undefined })
    )
  }

  const updateAddendum = useCallback(() => {
    if (!study || !content) return Promise.resolve()
    const report = content.filter(e=>e.ref?.current).map(e=>({label: e.label, html: (!isEditorEmpty(e.ref.current) && e.ref.current.getContent()) || null}))
    return UpdateAddendum(study.id, { report, autoSave: autoSaveRef.current }).then(() => setChanges(false)).catch(e => e)
  }, [study, content])

  const goBack = useCallback(num => {
    if (window.history.state && window.history.state.idx > 0) navigate(num)
    else navigate('/', { replace: true })
  }, [navigate])

  useEffect(() => {
    if (!didRun.current && params.studyId !== study?.id) {
      didRun.current = true
      GetStudy(params.studyId).then(resp => {
        setContent(() => {
          if (Array.isArray(resp.addendum) && resp.addendum.length) return resp.addendum
          else if (Array.isArray(resp.org?.settings?.addendumFields) && resp.org.settings.addendumFields.length) return resp.org.settings.addendumFields.map(label=>({label}))
          return defaultFields
        })
        setStudy(resp)
      }).catch(e=>e)
    }
      return () => {
        if (changeTimeout) {
          clearTimeout(changeTimeout)
          changeTimeout = undefined 
        }
      }
  }, [params.studyId, study])

  useEffect(() => {
    if (autoSave) {
      autoSaveRef.current = true
      updateAddendum().then(() => {
        autoSaveRef.current = false
        console.log('Auto saved')
      })
      setAutoSave()
    }
  }, [updateAddendum, autoSave])

  usePrompt("There are unsaved changes, are you sure you want to leave the page?", changes)

  if (!study) return <React.Fragment />

  const setEditorRef = (index, ref) => {
    setContent(content => {
      content[index].ref = ref
      return content
    })
  }

  const editorChange = () => {
    if (!changeTimeout)
      changeTimeout = setTimeout(() => {
        setAutoSave(true)
        changeTimeout = undefined
      }, 15000)
  }

  const handleSubmit = (e) => {
    e.preventDefault();

    updateAddendum().then(() => goBack(-1));
  };

  const handlePdfPreviewDialog = () => {
    updateAddendum().then(() =>
      setPdfPreviewDialogState({ open: true, studyId: `${study.id}` })
    )
  };

  const handleSaveDialog = () => {
    if (newField.length) {
      setContent((e) => [...e, { label: newField }])
      setOpen(false)
    }
  }

  const completeStudy = (id) => {
    updateAddendum().then(() =>
      CompleteStudy(id).then(() => goBack(-1)).catch(e=>e)
    )
  }

  const deleteField = (label, index) =>
    setConfirm(
      (i) =>
        (i = {
          open: true,
          text: `Are you sure you want to delete: ${label}?`,
          callback: () => {
            setContent(e => {
              const c = [...e]
              c.splice(index, 1)
              return c
            })
          }
        })
      )

  return (
    <Container>
      <ReportingData study={study} section="addendum" studyId={study.id} />
      <form onSubmit={handleSubmit}>
        <Grid
          sx={{ mb: 3, mt: 3 }}
          container
          spacing={4}
          justifyContent="left"
        >
          {content?.map((item, index) => (
            <FormControl key={item.label || index} fullWidth variant="standard">
              <Grid container direction="row">
                <Grid size={11}>
                  <Typography component="h2" variant="h6" color={(item.label && item.label[0] === '*' && 'secondary') || 'default'}>
                    {(item.label ? (item.label[0] === '*' && item.label.slice(1)) || item.label : '')}:
                </Typography>
                </Grid>
                <Grid size={1}>
                  <Button color="inherit" onClick={()=> deleteField(item.label, index)}>
                    <DeleteIcon />
                  </Button>
                </Grid>
              </Grid>
              <RichText
                onEditorRef={ref => setEditorRef(index, ref)}
                initialValue={item.html || '<p></p>'}
                onEditorChange={editorChange}
              />
            </FormControl>
          ))}
        </Grid>
        <Grid container sx={{ mb: 3 }} spacing={3} justifyContent="center">
          <FormControl fullWidth variant="standard">
            <Button color="secondary" onClick={()=> setOpen(true)} startIcon={<TextFieldsIcon />}>
              Add Field
            </Button>
          </FormControl>
          <Dialog open={open} maxWidth="sm" fullWidth onClose={()=> setOpen(false)}>
            <DialogTitle>Add Field</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Add a rich text field to the report for this study
              </DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                id="desc"
                label="Field Name"
                type="desc"
                fullWidth
                variant="standard"
                onChange={(e) => setNewField(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleSaveDialog}>Save</Button>
              <Button onClick={()=> setOpen(false)}>Cancel</Button>
            </DialogActions>
          </Dialog>
        </Grid>
        <Grid container spacing={3} justifyContent="left">
            <Button variant="outlined" color="primary" type="submit" startIcon={<SaveIcon />}>
              Save
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => completeStudy(study.id)}
              startIcon={<CompleteIcon />}
              sx={{"&:disabled": {backgroundColor: 'rgba(206, 147, 216, 0.25)'}}}
            >
              Complete
            </Button>
            <Button
              variant="outlined"
              color="success"
              onClick={handlePdfPreviewDialog}
              startIcon={<PictureAsPdfIcon />}
            >
              Pdf Preview
            </Button>
        </Grid>
      </form>
      <PdfPreview
        open={pdfPreviewDialogState.open}
        studyId={pdfPreviewDialogState.studyId}
        setDialogState={setPdfPreviewDialogState}
      />
      <Confirmation
        open={confirm.open}
        onCancel={confirmCancel}
        onConfirm={confirmOk}
      >
        {confirm.text}
      </Confirmation>
    </Container>
  );
};

export default StudyAddendum;
